import { z } from 'zod'

/** Accounting push to Xero or QuickBooks Online (design 7.6; apps/api/src/services/accountingSync.ts). */
export type AccountingProvider = 'xero' | 'quickbooks'
export type AccountingSyncKind = 'contact' | 'invoice' | 'invoice_void' | 'payment' | 'refund'
export type AccountingSyncStatus = 'pending' | 'synced' | 'failed' | 'skipped'

export const AccountingSettings = z.object({
  // Xero: the sales account, tax types for taxed and tax-free lines, and the bank account payments and refunds go to.
  sales_account_code: z.string().trim().max(10).nullish(),
  tax_type: z.string().trim().max(40).nullish(),
  tax_free_type: z.string().trim().max(40).nullish(),
  payment_account_code: z.string().trim().max(10).nullish(),
  // QuickBooks: the item (product/service) lines are booked to, tax code ids, and the bank account for payments and refunds.
  item_id: z.string().trim().max(40).nullish(),
  tax_code_id: z.string().trim().max(40).nullish(),
  tax_free_code_id: z.string().trim().max(40).nullish(),
  deposit_account_id: z.string().trim().max(40).nullish(),
})
export type AccountingSettings = z.infer<typeof AccountingSettings>

export const AccountingConnect = z.object({ provider: z.enum(['xero', 'quickbooks']) })
export const AccountingBackfill = z.object({ from: z.iso.date() })

export interface AccountingProvidersView {
  xero: boolean
  quickbooks: boolean
  quickbooks_environment: 'production' | 'sandbox'
  redirect_uris: { xero: string; quickbooks: string }
}

export interface AccountingConnectionView {
  id: string
  provider: AccountingProvider
  status: 'pending' | 'connected' | 'error' | 'disconnected'
  tenant_name: string | null
  environment: string | null
  settings: AccountingSettings
  /** What is missing before payments or refunds can be pushed (empty when nothing is). */
  missing_settings: string[]
  sync_from: string | null
  last_error: string | null
  connected_at: string | null
  last_synced_at: string | null
  last_reconciled_at: string | null
  counts: { pending: number; synced: number; failed: number; skipped: number }
}

export interface AccountingSyncItemView {
  id: string
  provider: AccountingProvider
  kind: AccountingSyncKind
  entity_id: string
  label: string | null
  invoice_id: string | null
  status: AccountingSyncStatus
  attempts: number
  remote_id: string | null
  last_error: string | null
  next_at: string | null
  synced_at: string | null
  updated_at: string
}

export interface AccountingOverview {
  providers: AccountingProvidersView
  connection: AccountingConnectionView | null
}
